import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, filter, takeUntil, tap } from 'rxjs/operators';
import { OrgDraft, OrgDraftStep } from '@newgenus/common';
import { OrgDraftService } from './services/org-draft.service';
import { OrgWizardStateService } from './services/org-wizard-state.service';
import { OrgCreationService } from './services/org-creation.service';
import {
    DraftsPickerDialogComponent,
    DraftsPickerData,
    DraftsPickerResult,
} from './dialogs/drafts-picker-dialog.component';

/**
 * Recursively strip `undefined` values from an object/array tree so the
 * result is safe to send to Firestore (which rejects `undefined` field
 * values with "Function DocumentReference.update() called with invalid data").
 * Plain objects and arrays are walked; primitives, Dates, and Firestore
 * Timestamps pass through untouched.
 */
function stripUndefined<T>(value: T): T {
    if (value === undefined || value === null) return value;
    if (Array.isArray(value)) {
        return value.map((v) => stripUndefined(v)) as unknown as T;
    }
    if (typeof value === 'object') {
        // Leave class instances (Date, Timestamp, etc.) alone.
        const proto = Object.getPrototypeOf(value);
        if (proto !== Object.prototype && proto !== null) return value;
        const out: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
            if (v === undefined) continue;
            out[k] = stripUndefined(v);
        }
        return out as unknown as T;
    }
    return value;
}

/**
 * Full-screen, dialog-styled wizard shell.
 *
 * Plan reference: §4.1, §4.2, §7, §10.
 *
 * P1 step coverage:
 *  - Step 1 — Source              ✅ (Blank + Scaffold; Template / Clone / Excel disabled)
 *  - Step 2 — Basics              ✅
 *  - Step 3 — Structure           ✅ (tree editor with drag-drop)
 *  - Step 4 — Staff               ✅ (email-anchored table with debounced checkEmail)
 *  - Step 5 — Plan & Features     ⏳ placeholder ("Coming in P2")
 *  - Step 6 — Review              ✅ (submits via `createFromDraft`)
 */
@Component({
    selector: 'newgenus-org-creation-wizard',
    templateUrl: './org-creation-wizard.component.html',
    styleUrls: ['./org-creation-wizard.component.scss'],
    providers: [OrgWizardStateService],
    standalone: false,
})
export class OrgCreationWizardComponent implements OnInit, OnDestroy {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private dialog = inject(MatDialog);
    private snack = inject(MatSnackBar);

    public draftService = inject(OrgDraftService);
    public state = inject(OrgWizardStateService);
    public creation = inject(OrgCreationService);

    private destroy$ = new Subject<void>();
    private autosaveSub?: Subscription;

    public loading = true;
    public submitting = false;
    public errorMessage: string | null = null;
    public lastSavedAt: Date | null = null;
    /**
     * Live autosave status surfaced in the header pill so the user can see
     * unsaved edits in flight before refreshing. Transitions:
     *   idle  → (edit) → dirty (debounce window) → saving → saved → idle
     *                                                    └→ error
     */
    public saveStatus: 'idle' | 'dirty' | 'saving' | 'saved' | 'error' = 'idle';
    private savedFadeTimer: ReturnType<typeof setTimeout> | null = null;
    public readonly stepNumbers: OrgDraftStep[] = [1, 2, 3, 4, 5, 6];

    public async ngOnInit(): Promise<void> {
        // Wire autosave: debounce changes and patch the draft in Firestore.
        // The first tap (pre-debounce) flips us into the "dirty" state so the
        // user can see unsaved edits in flight; the subscriber then flips to
        // "saving" → "saved" → "idle" around the actual write.
        this.autosaveSub = this.state.change$
            .pipe(
                // Skip the initial 'load' replay and pure step-navigation
                // emissions \u2014 hopping between steps without making an edit
                // shouldn't trigger a Firestore write or flash the saving pill.
                // The next real edit will persist the updated currentStep
                // alongside whatever else changed.
                filter((e) => e.source !== 'load' && e.source !== 'navigate'),
                tap(() => {
                    this.saveStatus = 'dirty';
                    if (this.savedFadeTimer) {
                        clearTimeout(this.savedFadeTimer);
                        this.savedFadeTimer = null;
                    }
                }),
                debounceTime(500),
                takeUntil(this.destroy$)
            )
            .subscribe(async ({ draft }) => {
                this.saveStatus = 'saving';
                try {
                    const { key, modifiedBy, modifiedOn, ...rest } = draft;
                    // Persist the transient UI snapshot alongside the draft so
                    // Step 3's per-node builder markers (moved/dirty/isNew/
                    // colour) survive a page refresh — the persisted draft is
                    // re-hydrated into wizard state via load() below.
                    const ui = this.state.uiSnapshot;
                    // Persist undo/redo history too, so the top-bar arrows
                    // keep working after a refresh on every step (Basics edits,
                    // Staff rows, Feature toggles, and Step 3 structural edits).
                    const history = this.state.exportHistory() as unknown as OrgDraft['history'];
                    const rawPayload: Partial<OrgDraft> = {
                        ...rest,
                        ...(ui ? { uiSnapshot: ui as OrgDraft['uiSnapshot'] } : {}),
                        ...(history ? { history } : {}),
                    };
                    // Firestore rejects any `undefined` field value with
                    // "Function DocumentReference.update() called with invalid
                    // data". Step 2's empty description input, drafts that
                    // never visited a step, and history entries that captured
                    // a not-yet-set field can all leak `undefined` into the
                    // payload. Strip them recursively before patching.
                    const payload = stripUndefined(rawPayload) as Partial<OrgDraft>;
                    await this.draftService.patch(key, payload);
                    this.lastSavedAt = new Date();
                    this.saveStatus = 'saved';
                    // Drop back to idle after a short hold so "saved" doesn't
                    // linger forever and let the next edit cycle through dirty.
                    this.savedFadeTimer = setTimeout(() => {
                        if (this.saveStatus === 'saved') this.saveStatus = 'idle';
                        this.savedFadeTimer = null;
                    }, 1500);
                } catch (err) {
                    this.saveStatus = 'error';
                    console.error('OrgCreationWizardComponent autosave error:', err);
                }
            });

        const draftKey = this.route.snapshot.paramMap.get('draftKey');
        try {
            if (draftKey) {
                await this.loadExisting(draftKey);
            } else {
                await this.startNew();
            }
        } catch (err: unknown) {
            console.error('OrgCreationWizardComponent init failed:', err);
            const msg = err instanceof Error ? err.message : 'Unknown error';
            this.errorMessage = `Couldn't open the wizard: ${msg}`;
        } finally {
            this.loading = false;
        }
    }

    public ngOnDestroy(): void {
        this.autosaveSub?.unsubscribe();
        if (this.savedFadeTimer) {
            clearTimeout(this.savedFadeTimer);
            this.savedFadeTimer = null;
        }
        this.state.clear();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private async loadExisting(draftKey: string): Promise<void> {
        let draft: OrgDraft | null = null;
        try {
            draft = await this.draftService.get(draftKey);
        } catch (err: unknown) {
            // A draft that has been deleted (or that the current user doesn't
            // own) trips the Firestore security rules with a permission-denied
            // error rather than a missing-doc null. Treat both the same: drop
            // the user back on the org dashboard with a friendly notice
            // instead of leaving them stranded on the broken wizard URL.
            const code = (err as { code?: string } | null)?.code ?? '';
            if (code === 'permission-denied' || code === 'not-found') {
                draft = null;
            } else {
                throw err;
            }
        }
        if (!draft) {
            this.snack.open('That draft is no longer available.', 'OK', { duration: 4000 });
            await this.router.navigate(['dashboard'], { relativeTo: this.route.parent });
            return;
        }
        if (draft.status !== 'in-progress' && draft.status !== 'partial-recovery') {
            this.snack.open('This draft has already been submitted.', 'OK', { duration: 4000 });
            await this.router.navigate(['dashboard'], { relativeTo: this.route.parent });
            return;
        }
        this.state.load(draft);
    }

    private async startNew(): Promise<void> {
        const result = await this.draftService.startDraft();
        if (result.kind === 'cap-reached') {
            await this.handleCapReached(result.drafts);
            return;
        }
        await this.router.navigate(['new', result.draft.key], { relativeTo: this.route.parent, replaceUrl: true });
        this.state.load(result.draft);
    }

    /**
     * Open the drafts-picker dialog when the user is at the D11 cap. The user
     * can either pick an existing draft to resume, trash one to free a slot
     * (we then retry `startDraft()` automatically), or cancel out back to the
     * dashboard.
     */
    private async handleCapReached(drafts: OrgDraft[]): Promise<void> {
        const ref = this.dialog.open<DraftsPickerDialogComponent, DraftsPickerData, DraftsPickerResult>(
            DraftsPickerDialogComponent,
            {
                data: { drafts },
                width: '640px',
                maxWidth: '90vw',
                disableClose: false,
                autoFocus: false,
            }
        );
        const result = await ref.afterClosed().toPromise();
        if (!result || result.action === 'cancel') {
            await this.router.navigate(['dashboard'], { relativeTo: this.route.parent });
            return;
        }
        if (result.action === 'open') {
            await this.router.navigate(['new', result.draftKey], {
                relativeTo: this.route.parent,
                replaceUrl: true,
            });
            const draft = await this.draftService.get(result.draftKey);
            if (draft) {
                this.state.load(draft);
            }
            return;
        }
        if (result.action === 'trashed') {
            // A slot was freed — retry the new-draft flow.
            this.loading = true;
            try {
                await this.startNew();
            } finally {
                this.loading = false;
            }
        }
    }

    public goToStep(step: OrgDraftStep): void {
        this.state.setStep(step);
    }

    public next(): void {
        const cur = this.state.snapshot?.currentStep ?? 1;
        if (cur < 6) this.goToStep((cur + 1) as OrgDraftStep);
    }

    public previous(): void {
        const cur = this.state.snapshot?.currentStep ?? 1;
        if (cur > 1) this.goToStep((cur - 1) as OrgDraftStep);
    }

    public canAdvanceFrom(step: OrgDraftStep, draft: OrgDraft | null): boolean {
        if (!draft) return false;
        switch (step) {
            case 1:
                return !!draft.sourceMode;
            case 2:
                return !!draft.basics?.name?.trim();
            case 3: {
                // Plan addendum 19: cannot advance until exactly one post is
                // crowned as CEO. Drafts with no posts at all also fail this
                // gate — the org creator's CEO hat needs a post to attach to.
                const posts = draft.structure.filter((p) => p.partType === 'post');
                if (posts.length === 0) return false;
                return posts.some((p) => (p as { isCeoPost?: boolean }).isCeoPost === true);
            }
            // Plan addendum 20 — Step 4 gate (Phase B.2b, spec §6.5):
            // block Next while any row is still `unresolved` or `invalid`.
            // Step 4's banner mirrors this state and offers a one-click recheck.
            case 4: {
                const staff = draft.staff ?? [];
                if (staff.some((r) => r.resolutionStatus === 'unresolved' || r.resolutionStatus === 'invalid')) {
                    return false;
                }
                return true;
            }
            case 5:
                return true;
            case 6:
                return false;
        }
    }

    public async submit(draft: OrgDraft): Promise<void> {
        if (this.submitting) return;
        this.submitting = true;
        this.errorMessage = null;
        try {
            const resp = await new Promise<{ orgKey: string }>((resolve, reject) => {
                this.creation
                    .createFromDraft(draft.key)
                    .pipe(
                        takeUntil(this.destroy$),
                        catchError((err) => {
                            reject(err);
                            return of(null);
                        })
                    )
                    .subscribe((r) => r && resolve(r));
            });
            this.snack.open(`Organization "${draft.basics.name}" created.`, 'OK', { duration: 4000 });
            // Land on the new org's board (D13).
            await this.router.navigate(['dashboard'], { relativeTo: this.route.parent });
        } catch (err: any) {
            console.error('createFromDraft failed:', err);
            this.errorMessage =
                err?.error?.message || err?.message || 'Failed to create organization. Please try again.';
        } finally {
            this.submitting = false;
        }
    }

    public async closeWizard(): Promise<void> {
        // Autosave already kept us up-to-date. Just route away.
        await this.router.navigate(['dashboard'], { relativeTo: this.route.parent });
    }
}
